import { useEffect, useState } from 'react';
import { supabase } from '../services/supabase';

export type NotifType = 'payment_received' | 'help_requested' | 'cash_pending' | 'table_freed';

export interface StaffNotif {
  id: string;
  type: NotifType;
  table_number: number | null;
  amount: number | null;
  channel: string | null;
  created_at: string;
  read: boolean;
}

export function useNotifications(storeId: string) {
  const [notifs, setNotifs] = useState<StaffNotif[]>([]);

  useEffect(() => {
    if (!storeId) return;

    const ordersChannel = supabase
      .channel(`staff-notifs-${storeId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'orders', filter: `store_id=eq.${storeId}` },
        async (payload) => {
          const row = payload.new as Record<string, unknown>;
          const info = row.customer_info as Record<string, unknown> | null;
          if (!info?.source?.toString().startsWith('tapnpay')) return;

          let tableNumber: number | null = null;
          if (row.table_id) {
            const { data } = await supabase
              .from('restaurant_tables')
              .select('table_number')
              .eq('id', row.table_id)
              .maybeSingle();
            tableNumber = (data as { table_number?: number } | null)?.table_number ?? null;
          }

          const method = row.payment_method as string | null;
          let type: NotifType = 'payment_received';
          if (info?.payment_status === 'pending_cash') type = 'cash_pending';

          const notif: StaffNotif = {
            id: `order-${row.id as string}`,
            type,
            table_number: tableNumber,
            amount: (row.total_amount as number) ?? null,
            channel: method,
            created_at: row.created_at as string,
            read: false,
          };

          playSound();
          setNotifs((prev) => [notif, ...prev].slice(0, 50));
        },
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'table_requests', filter: `store_id=eq.${storeId}` },
        async (payload) => {
          const row = payload.new as Record<string, unknown>;
          let tableNumber: number | null = null;
          if (row.table_id) {
            const { data } = await supabase
              .from('restaurant_tables')
              .select('table_number')
              .eq('id', row.table_id)
              .maybeSingle();
            tableNumber = (data as { table_number?: number } | null)?.table_number ?? null;
          }
          const notif: StaffNotif = {
            id: `req-${row.id as string}`,
            type: 'help_requested',
            table_number: tableNumber,
            amount: null,
            channel: null,
            created_at: row.created_at as string,
            read: false,
          };
          playSound();
          setNotifs((prev) => [notif, ...prev].slice(0, 50));
        },
      )
      .subscribe();

    return () => { void supabase.removeChannel(ordersChannel); };
  }, [storeId]);

  const markRead = (id: string) => {
    setNotifs((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  };

  const markAllRead = () => {
    setNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  return { notifs, unreadCount: notifs.filter((n) => !n.read).length, markRead, markAllRead };
}

function playSound() {
  try {
    const audio = new Audio('/sounds/order-notification.mp3');
    audio.play().catch(() => undefined);
  } catch {
    // Sound is best-effort
  }
}
